import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth, useAuthState, AUTH_PHASE } from "@/contexts/AuthContext";
import { FxProvider } from "@/contexts/FxContext";
import { LoginPage, RegisterPage } from "@/pages/Auth";
import { DashboardPage } from "@/pages/Dashboard";
import { MemberDashboard } from "@/pages/MemberDashboard";
import { MyPoolsPage } from "@/pages/MyPools";
import { PayoutHistoryPage } from "@/pages/PayoutHistoryPage";
import { SettingsPage } from "@/pages/Settings";
import { ApprovalsPage } from "@/pages/Approvals";
import { FaqPage } from "@/pages/FaqPage";
import { AuditLogPage } from "@/pages/AuditLog";
import { BillingPage } from "@/pages/BillingPage";
import { InviteCenterPage } from "@/pages/InviteCenterPage";
import { InviteAcceptPage } from "@/pages/InviteAccept";
import { AdminDashboardPage } from "@/pages/admin/AdminDashboardPage";
import { AdminUsersPage } from "@/pages/admin/AdminUsersPage";
import { AdminOrgsPage } from "@/pages/admin/AdminOrgsPage";
import { AdminAuditLogsPage } from "@/pages/admin/AdminAuditLogsPage";
import { AdminTrafficPage } from "@/pages/admin/AdminTrafficPage";
import { AdminDatabasePage } from "@/pages/admin/AdminDatabasePage";
import { AdminPoolsPage } from "@/pages/admin/AdminPoolsPage";
import { AdminSecurityPage } from "@/pages/admin/AdminSecurityPage";
import { AdminEmailsPage } from "@/pages/admin/AdminEmailsPage";
import { AdminSitePage } from "@/pages/admin/AdminSitePage";
import { TermsOfServicePage } from "@/pages/TermsOfService";
import { PrivacyPolicyPage } from "@/pages/PrivacyPolicy";
import { AppLayout } from "@/components/AppLayout";
import { AuthRedirectEffect } from "@/components/AuthRedirectEffect";
import { AdminRouteGuard, AppRouteGuard } from "@/components/AdminRouteGuard";
import { ProtectedPageGate } from "@/components/ProtectedPageGate";
import {
    ADMIN_DASHBOARD,
    ADMIN_LOGIN,
    APP_DASHBOARD,
    APP_LOGIN,
    getAuthenticatedLoginRedirect,
    getPostLoginPath,
    isAdminSession,
    isAuthPending,
    isAuthenticatedUser,
    isSuperadmin,
} from "@/lib/authRouting";
import { isAuthPath } from "@/lib/layoutRouting";
import { AppRecoveryBoundary } from "@/components/AppRecoveryBoundary";
import { ClientLifecycle } from "@/components/ClientLifecycle";
import { Toaster } from "sonner";
import { SiteConfigProvider } from "@/contexts/SiteConfigContext";

function RootRedirect() {
    const { user, authPhase } = useAuth();
    if (isAuthPending(authPhase)) {
        return <div className="route-redirect-pending w-full min-h-[min(28rem,calc(100vh-8rem))]" aria-hidden data-testid="root-redirect-pending" />;
    }
    if (!isAuthenticatedUser(user)) return <Navigate to={APP_LOGIN} replace />;
    if (isSuperadmin(user)) return <Navigate to={ADMIN_DASHBOARD} replace />;
    return <Navigate to={APP_DASHBOARD} replace />;
}

function AdminEntryRedirect() {
    const { user, authPhase } = useAuth();
    if (isAuthPending(authPhase)) {
        return <div className="route-redirect-pending w-full min-h-[min(28rem,calc(100vh-8rem))]" aria-hidden data-testid="admin-entry-redirect-pending" />;
    }
    if (!isAuthenticatedUser(user)) return <Navigate to={ADMIN_LOGIN} replace />;
    if (isSuperadmin(user)) return <Navigate to={ADMIN_DASHBOARD} replace />;
    return <Navigate to={APP_DASHBOARD} replace />;
}

function LoginRoute({ adminMode = false }) {
    const { user, authPhase, sessionType } = useAuthState();
    const location = useLocation();

    if (authPhase === AUTH_PHASE.AUTHENTICATED && isAuthenticatedUser(user)) {
        if (isSuperadmin(user)) {
            if (!adminMode && sessionType !== "admin") {
                return <Navigate to={ADMIN_LOGIN} replace />;
            }
            return <Navigate to={ADMIN_DASHBOARD} replace />;
        }
        if (adminMode) {
            return <Navigate to={APP_DASHBOARD} replace />;
        }
        return <Navigate to={location.state?.from?.pathname || APP_DASHBOARD} replace />;
    }

    return <LoginPage adminMode={adminMode} />;
}

function RegisterRoute() {
    const { user, authPhase, sessionType } = useAuthState();
    const location = useLocation();

    if (authPhase === AUTH_PHASE.AUTHENTICATED && isAuthenticatedUser(user)) {
        return <Navigate to={getPostLoginPath(user, sessionType, location.state?.from?.pathname || APP_DASHBOARD)} replace />;
    }

    return <RegisterPage />;
}

function DashboardSwitch() {
    const { activeOrg, memberships } = useAuth();
    if (!(memberships || []).length || !activeOrg?.id) {
        return <DashboardPage />;
    }
    if (activeOrg.role === "member") return <MemberDashboard />;
    return <DashboardPage />;
}

function MemberGuard({ children }) {
    const { activeOrg } = useAuth();
    if (activeOrg?.role === "member") return <Navigate to={APP_DASHBOARD} replace />;
    return children;
}

function CatchAllRedirect() {
    const { user, authPhase, sessionType } = useAuth();
    if (isAuthPending(authPhase)) {
        return <div className="route-redirect-pending w-full min-h-[min(28rem,calc(100vh-8rem))]" aria-hidden data-testid="catchall-redirect-pending" />;
    }
    if (isAuthenticatedUser(user)) return <Navigate to={getPostLoginPath(user, sessionType)} replace />;
    return <Navigate to={APP_LOGIN} replace />;
}

function Protected({ children }) {
    return <ProtectedPageGate>{children}</ProtectedPageGate>;
}

function AuthAwareToaster() {
    const { pathname } = useLocation();
    if (isAuthPath(pathname)) return null;
    return (
        <Toaster
            theme="dark"
            position="bottom-right"
            toastOptions={{
                style: {
                    background: "#262626",
                    border: "1px solid rgba(255,255,255,0.08)",
                    color: "#ffffff",
                },
            }}
        />
    );
}

function AppRoutes() {
    return (
        <>
            <AuthRedirectEffect />
            <Routes>
                <Route path="/login" element={<LoginRoute adminMode={false} />} />
                <Route path="/register" element={<RegisterRoute />} />
                <Route path="/terms" element={<TermsOfServicePage />} />
                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/admin/login" element={<LoginRoute adminMode={true} />} />

                <Route element={<AppLayout />}>
                    <Route path="/" element={<RootRedirect />} />
                    <Route path="/admin" element={<AdminEntryRedirect />} />
                    <Route path="/invite/:token" element={<InviteAcceptPage />} />

                    <Route element={<AppRouteGuard />}>
                        <Route path="/dashboard" element={<Protected><DashboardSwitch /></Protected>} />
                        <Route path="/pools" element={<Protected><MemberGuard><MyPoolsPage /></MemberGuard></Protected>} />
                        <Route path="/payouts" element={<Protected><MemberGuard><PayoutHistoryPage /></MemberGuard></Protected>} />
                        <Route path="/approvals" element={<Protected><MemberGuard><ApprovalsPage /></MemberGuard></Protected>} />
                        <Route path="/audit" element={<Protected><MemberGuard><AuditLogPage /></MemberGuard></Protected>} />
                        <Route path="/billing" element={<Protected><MemberGuard><BillingPage /></MemberGuard></Protected>} />
                        <Route path="/invites" element={<Protected><InviteCenterPage /></Protected>} />
                        <Route path="/faq/*" element={<Protected><FaqPage /></Protected>} />
                        <Route path="/settings" element={<Protected><SettingsPage /></Protected>} />
                    </Route>

                    <Route element={<AdminRouteGuard />}>
                        <Route path="/admin/dashboard" element={<Protected><AdminDashboardPage /></Protected>} />
                        <Route path="/admin/users" element={<Protected><AdminUsersPage /></Protected>} />
                        <Route path="/admin/orgs" element={<Protected><AdminOrgsPage /></Protected>} />
                        <Route path="/admin/pools" element={<Protected><AdminPoolsPage /></Protected>} />
                        <Route path="/admin/traffic" element={<Protected><AdminTrafficPage /></Protected>} />
                        <Route path="/admin/emails" element={<Protected><AdminEmailsPage /></Protected>} />
                        <Route path="/admin/site" element={<Protected><AdminSitePage /></Protected>} />
                        <Route path="/admin/database" element={<Protected><AdminDatabasePage /></Protected>} />
                        <Route path="/admin/audit" element={<Protected><AdminAuditLogsPage /></Protected>} />
                        <Route path="/admin/security" element={<Protected><AdminSecurityPage /></Protected>} />
                    </Route>

                    <Route path="*" element={<CatchAllRedirect />} />
                </Route>
            </Routes>
        </>
    );
}

function App() {
    return (
        <AppRecoveryBoundary>
            <div className="App h-full min-h-0 overflow-hidden bg-[#1E1E1E]">
                <AuthProvider>
                    <ClientLifecycle />
                    <FxProvider>
                        <BrowserRouter>
                            <SiteConfigProvider>
                                <AppRoutes />
                                <AuthAwareToaster />
                            </SiteConfigProvider>
                        </BrowserRouter>
                    </FxProvider>
                </AuthProvider>
            </div>
        </AppRecoveryBoundary>
    );
}

export default App;
